import { useEffect, useMemo, useRef, useState } from 'react';

import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import type { DropdownIndicatorProps, GroupBase, OnChangeValue, SelectInstance } from 'react-select';
import { components } from 'react-select';
import type { AsyncProps } from 'react-select/async';
import AsyncSelect from 'react-select/async';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type { FilterOptionOption } from 'react-select/dist/declarations/src/filters';

import { useBoolean, useDebouncedPromise } from '@/_shared';

import type { RAsyncComboBoxProps } from './types';

import { LoadingIndicator } from '../react-select';
import { ClearIndicator } from '../react-select-clear-indicator';
import { componentsStyles } from '../react-select-component-style';
import { newContainer } from '../react-select-new-container';
import { newMultiValue } from '../react-select-new-multi-value';

export function AsyncComboBox<T extends { id: number }, IsMulti extends boolean = false>({
  label,
  labelProps,
  disabled = false,
  showBorder,
  perPage = 25,
  itemToString,
  keyExtractor,
  onValueChange,
  filterItems,
  getMultiValueLabel,
  useApi,
  apiParams = {},
  mutationOptions,
  filterId,
  isClearable = true,
  hideClearIndicator = false,
  getRef,
  menuListClassName,
  showSearchIcon,
  preload = false,
  loading = false,
  ...props
}: RAsyncComboBoxProps<T, IsMulti>) {
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const fetchDefaultOptions = useRef(true);
  const [isFocused, focus] = useBoolean();
  const enterPressedRef = useRef(false);
  const selectRef = useRef<SelectInstance<T, IsMulti, GroupBase<T>>>(null);

  const {
    mutateAsync: getOptions,
    data: optionsData,
    isPending,
  } = useApi({
    ...(mutationOptions || {}),
    onSuccess: () => {
      focus.setFalse();
    },
  });

  function focusNextElement() {
    const focussableElements =
      'a:not([disabled]), button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';

    if (document.activeElement && 'form' in document.activeElement) {
      const formElement = document.activeElement.form as HTMLFormElement;
      const focussable = Array.from(formElement.querySelectorAll(focussableElements)).filter(
        (element) =>
          (element as HTMLElement).offsetWidth > 0 ||
          (element as HTMLElement).offsetHeight > 0 ||
          element === document.activeElement
      );

      const index = focussable.indexOf(document.activeElement);
      if (index > -1) {
        const nextElement = focussable[index + 1] || focussable[0];
        (nextElement as HTMLElement).focus();
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      focusNextElement();
    } else if (event.key === 'Enter') {
      enterPressedRef.current = true;
    }
  };

  const ControlComponent = useMemo(
    () => newContainer({ label, labelProps, showBorder }),
    [label, labelProps, showBorder]
  );

  const MultiValueComponent = useMemo(() => newMultiValue({ getMultiValueLabel }), [getMultiValueLabel]);

  const fetchOptionsDebounced = useDebouncedPromise(async (inputValue: string) => {
    if (disabled) {
      return [];
    }
    fetchDefaultOptions.current = true;
    const res = await getOptions({ search: inputValue, per_page: perPage, ...apiParams });
    if (enterPressedRef.current && res.length === 1 && onValueChange) {
      onValueChange(res[0] as OnChangeValue<T, IsMulti>, { action: 'select-option', option: res[0] as T });
      selectRef.current?.blur();
    }
    enterPressedRef.current = false;
    return filterItems ? res.filter(filterItems) : res;
  }, 500);

  const filterOptionHandler: AsyncProps<T, IsMulti, GroupBase<T>>['filterOption'] = useMemo(() => {
    if (filterItems) {
      return (data: FilterOptionOption<T>) => filterItems(data.data);
    }
    if (filterId) {
      return (data: FilterOptionOption<T>) => data.data.id !== filterId;
    }
    return undefined;
  }, [filterId, filterItems]);

  const CustomDropdownIndicator = (props: DropdownIndicatorProps<T, IsMulti, GroupBase<T>>) => {
    return (
      <components.DropdownIndicator {...props}>
        {showSearchIcon ? <SearchIcon className="size-4" /> : <ChevronDownIcon className="size-4" />}
      </components.DropdownIndicator>
    );
  };

  useEffect(() => {
    if (isFocused && fetchDefaultOptions.current) {
      fetchDefaultOptions.current = false;
      getOptions({ search: '', per_page: perPage, ...apiParams });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, perPage, apiParams]);

  useEffect(() => {
    if (getRef) {
      getRef(selectRef.current as HTMLInputElement | null);
    }
  }, [getRef]);

  useEffect(() => {
    if (!preload || disabled) return;
    void getOptions({ search: '', per_page: perPage, ...apiParams });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preload triggers one-off list fetch when opened
  }, [preload]);

  return (
    <AsyncSelect
      ref={selectRef}
      hideSelectedOptions
      isDisabled={disabled}
      components={{
        Control: ControlComponent,
        LoadingIndicator,
        MultiValue: MultiValueComponent,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ClearIndicator: hasSearchValue || !hideClearIndicator ? ClearIndicator : undefined,
        DropdownIndicator: CustomDropdownIndicator,
      }}
      onInputChange={(newValue) => {
        setHasSearchValue(newValue?.length > 0);
      }}
      defaultOptions={filterItems ? optionsData?.filter(filterItems) : optionsData}
      isLoading={isPending || loading}
      getOptionLabel={itemToString as AsyncProps<T, IsMulti, GroupBase<T>>['getOptionLabel']}
      getOptionValue={keyExtractor}
      loadOptions={fetchOptionsDebounced}
      filterOption={filterOptionHandler}
      onFocus={focus.setTrue}
      onBlur={focus.setFalse}
      onChange={onValueChange}
      {...props}
      isClearable={isClearable}
      classNames={componentsStyles(props.isMulti, disabled, undefined, menuListClassName)}
      menuShouldScrollIntoView={false}
      menuPlacement="auto"
      onKeyDown={handleKeyDown}
      openMenuOnFocus
      menuPortalTarget={
        typeof props.menuPortalTarget === 'undefined'
          ? document.getElementById('portal-root') || document.body
          : props.menuPortalTarget
      }
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
    />
  );
}
